import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  CardActionArea,
  Chip,
  IconButton,
  Popover
} from '@mui/material';
import {
  PrivacyTip as PrivacyIcon,
  HealthAndSafety as HipaaIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  ArrowForward as ArrowForwardIcon,
  List as RequirementListIcon,
  Cloud as ServicesIcon,
  Gavel as DecisionsIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get } from '../../services/apiService';

const darkGreenColor = "#1D4434";

/**
 * HIPAA/CUI Progress Dashboard - Shows controls-wise progress cards
 * @returns {JSX.Element} HipaaCuiProgressDashboard component
 */
const PROGRESS_HELP = (
  <>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Click on any card below to view detailed progress and filter results.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Progress is calculated from the Work Items collection using the Epics configured in HIPAA/CUI settings (Privacy, HIPAA, and Security Control Epic IDs). Completion % and story points are derived from Features, Backlog Items, and Tasks under each Epic.
    </Typography>
  </>
);

const ASSETS_HELP = (
  <Typography variant="body2">
    Click on any card below to view detailed assets information (Decisions, Requirements, Services).
  </Typography>
);

const HipaaCuiProgressDashboard = ({ onCardClick }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [environmentBuildData, setEnvironmentBuildData] = useState(null);
  const [assetsData, setAssetsData] = useState({
    requirements: null,
    services: null,
    decisions: null
  });
  const [helpAnchor, setHelpAnchor] = useState(null);
  const [helpContent, setHelpContent] = useState(null);

  const openHelp = (event, content) => {
    setHelpAnchor(event.currentTarget);
    setHelpContent(content);
  };
  const closeHelp = () => {
    setHelpAnchor(null);
    setHelpContent(null);
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashboardResponse, environmentBuildResponse, requirementsResponse, servicesResponse, decisionsResponse] = await Promise.all([
        get('/api/hipaa-cui/dashboard'),
        get('/api/hipaa-cui/environment-build'),
        get('/api/hipaa-cui/requirements'),
        get('/api/hipaa-cui/services'),
        get('/api/hipaa-cui/decisions')
      ]);
      
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data);
      } else {
        setError('Failed to load dashboard data');
      }

      if (environmentBuildResponse.success && environmentBuildResponse.data) {
        setEnvironmentBuildData(environmentBuildResponse.data);
      }

      // Load assets data
      if (requirementsResponse.success) {
        setAssetsData(prev => ({ ...prev, requirements: requirementsResponse.data || [] }));
      }
      if (servicesResponse.success) {
        setAssetsData(prev => ({ ...prev, services: servicesResponse.data || [] }));
      }
      if (decisionsResponse.success) {
        setAssetsData(prev => ({ ...prev, decisions: decisionsResponse.data || [] }));
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (controlType) => {
    // Navigate to Product Engineering tab with filter
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', 'product-engineering');
    currentParams.set('filter', controlType);
    navigate(`?${currentParams.toString()}`, { replace: true });
    
    // Also trigger tab change if callback provided
    if (onCardClick) {
      onCardClick(null, 1); // Tab index 1 is Product Engineering
    }
  };

  const handleEnvironmentBuildClick = () => {
    // Navigate to Environment Build tab
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', 'environment-build');
    currentParams.delete('filter');
    navigate(`?${currentParams.toString()}`, { replace: true });
    
    // Also trigger tab change if callback provided
    if (onCardClick) {
      onCardClick(null, 2); // Tab index 2 is Environment Build
    }
  };

  const handleAssetsClick = (subTab = null) => {
    // Navigate to Assets tab
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', 'assets');
    if (subTab) {
      currentParams.set('subTab', subTab);
    }
    navigate(`?${currentParams.toString()}`, { replace: true });
    
    // Also trigger tab change if callback provided
    if (onCardClick) {
      onCardClick(null, 3); // Tab index 3 is Assets
    }
  };

  const renderControlCard = (title, icon, data, color, controlType) => {
    const completionRate = data?.avgCompletionRate || 0;
    const totalStoryPoints = data?.totalStoryPoints || 0;
    const completedStoryPoints = data?.completedStoryPoints || 0;
    const totalFeatures = data?.totalFeatures || 0;

    return (
      <Card 
        sx={{ 
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px ${color}40`,
            '& .card-icon': {
              transform: 'scale(1.1)',
            },
            '& .arrow-icon': {
              opacity: 1,
              transform: 'translateX(4px)',
            }
          }
        }}
        elevation={3}
      >
        <CardActionArea 
          onClick={() => handleCardClick(controlType)}
          sx={{ height: '100%', p: 0 }}
        >
          <CardContent sx={{ p: 3, pb: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  className="card-icon"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {React.createElement(icon, { 
                    sx: { color: color, fontSize: 32 } 
                  })}
                </Box>
                <Typography variant="h5" component="h2" fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              <ArrowForwardIcon 
                className="arrow-icon"
                sx={{ 
                  color: color, 
                  opacity: 0.5,
                  transition: 'all 0.3s ease',
                  fontSize: 20
                }} 
              />
            </Box>
            
            <Box mb={2.5}>
              <Typography 
                variant="h2" 
                fontWeight={700}
                sx={{ 
                  color: color,
                  mb: 1,
                  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {completionRate}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={completionRate} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: `${color}20`,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`
                  }
                }}
              />
            </Box>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Total Features
                  </Typography>
                  <Typography variant="h5" fontWeight={600} mt={0.5}>
                    {totalFeatures}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Story Points
                  </Typography>
                  <Typography variant="h5" fontWeight={600} mt={0.5}>
                    {completedStoryPoints} / {totalStoryPoints}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderAssetsCard = (title, icon, data, color, subTab) => {
    if (!data || data.length === 0) {
      return (
        <Card 
          sx={{ 
            height: '100%',
            position: 'relative',
            overflow: 'visible',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px ${color}40`,
            }
          }}
          elevation={3}
        >
          <CardActionArea 
            onClick={() => handleAssetsClick(subTab)}
            sx={{ height: '100%', p: 0 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: `${color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    {React.createElement(icon, { 
                      sx: { color: color, fontSize: 32 } 
                    })}
                  </Box>
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    {title}
                  </Typography>
                </Box>
                <ArrowForwardIcon 
                  sx={{ 
                    color: color, 
                    opacity: 0.5,
                    transition: 'all 0.3s ease',
                    fontSize: 20
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    }

    // Calculate statistics based on card type
    let totalCount = data.length;
    let inScopeCount = 0;
    let outOfScopeCount = 0;
    let completedCount = 0;
    let pendingCount = 0;
    let inProgressCount = 0;
    let dueCount = 0;
    let dueSoonCount = 0;

    if (subTab === 'requirements' || subTab === 'services') {
      inScopeCount = data.filter(item => item.inScope === 'In-Scope').length;
      outOfScopeCount = data.filter(item => item.inScope === 'Out-of-Scope').length;
    } else if (subTab === 'decisions') {
      completedCount = data.filter(item => item.status === 'Completed').length;
      pendingCount = data.filter(item => item.status === 'Pending').length;
      inProgressCount = data.filter(item => item.status === 'In Progress').length;
      
      // Calculate due and due soon decisions
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      data.forEach(item => {
        if (item.dueDate && item.status !== 'Completed' && item.status !== 'Cancelled') {
          const due = new Date(item.dueDate);
          due.setHours(0, 0, 0, 0);
          const diffTime = due - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays < 0) {
            dueCount++;
          } else if (diffDays >= 0 && diffDays <= 7) {
            dueSoonCount++;
          }
        }
      });
    }

    const completionRate = subTab === 'decisions' 
      ? totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
      : totalCount > 0 ? Math.round((inScopeCount / totalCount) * 100) : 0;

    return (
      <Card 
        sx={{ 
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px ${color}40`,
            '& .card-icon': {
              transform: 'scale(1.1)',
            },
            '& .arrow-icon': {
              opacity: 1,
              transform: 'translateX(4px)',
            }
          }
        }}
        elevation={3}
      >
        <CardActionArea 
          onClick={() => handleAssetsClick(subTab)}
          sx={{ height: '100%', p: 0 }}
        >
          <CardContent sx={{ p: 3, pb: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  className="card-icon"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {React.createElement(icon, { 
                    sx: { color: color, fontSize: 32 } 
                  })}
                </Box>
                <Typography variant="h5" component="h2" fontWeight={600}>
                  {title}
                </Typography>
              </Box>
              <ArrowForwardIcon 
                className="arrow-icon"
                sx={{ 
                  color: color, 
                  opacity: 0.5,
                  transition: 'all 0.3s ease',
                  fontSize: 20
                }} 
              />
            </Box>
            
            <Box mb={2.5}>
              <Typography 
                variant="h2" 
                fontWeight={700}
                sx={{ 
                  color: color,
                  mb: 1,
                  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {totalCount}
              </Typography>
              {subTab === 'decisions' ? (
                <LinearProgress 
                  variant="determinate" 
                  value={completionRate} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: `${color}20`,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`
                    }
                  }}
                />
              ) : (
                <LinearProgress 
                  variant="determinate" 
                  value={completionRate} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: `${color}20`,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`
                    }
                  }}
                />
              )}
            </Box>

            <Grid container spacing={2} mt={1}>
              {subTab === 'decisions' ? (
                <>
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: 'action.hover',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Completed
                      </Typography>
                      <Typography variant="h5" fontWeight={600} mt={0.5}>
                        {completedCount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: dueCount > 0 ? 'rgba(211, 47, 47, 0.08)' : 'action.hover',
                        transition: 'all 0.2s ease',
                        border: dueCount > 0 ? '1px solid' : 'none',
                        borderColor: dueCount > 0 ? 'rgba(211, 47, 47, 0.3)' : 'transparent',
                        '&:hover': {
                          bgcolor: dueCount > 0 ? 'rgba(211, 47, 47, 0.12)' : 'action.selected',
                        }
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Overdue
                      </Typography>
                      <Typography variant="h5" fontWeight={600} mt={0.5} sx={{ color: dueCount > 0 ? '#d32f2f' : 'inherit' }}>
                        {dueCount}
                      </Typography>
                    </Box>
                  </Grid>
                  {dueSoonCount > 0 && (
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 2, 
                          bgcolor: 'warning.light',
                          border: '2px solid',
                          borderColor: 'warning.main',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'warning.main',
                            '& .due-soon-text': {
                              color: 'white'
                            }
                          }
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Due Soon (within 7 days)
                        </Typography>
                        <Typography variant="h5" fontWeight={600} mt={0.5} className="due-soon-text" sx={{ color: 'warning.dark' }}>
                          {dueSoonCount}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: 'action.hover',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        In-Scope
                      </Typography>
                      <Typography variant="h5" fontWeight={600} mt={0.5}>
                        {inScopeCount}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        bgcolor: 'action.hover',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Out-of-Scope
                      </Typography>
                      <Typography variant="h5" fontWeight={600} mt={0.5}>
                        {outOfScopeCount}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderEnvironmentBuildCard = () => {
    if (!environmentBuildData) {
      return (
        <Card 
          sx={{ 
            height: '100%',
            position: 'relative',
            overflow: 'visible',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px ${darkGreenColor}40`,
            }
          }}
          elevation={3}
        >
          <CardActionArea 
            onClick={handleEnvironmentBuildClick}
            sx={{ height: '100%', p: 0 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: `${darkGreenColor}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <BuildIcon sx={{ color: darkGreenColor, fontSize: 32 }} />
                  </Box>
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    Environment Build
                  </Typography>
                </Box>
                <ArrowForwardIcon 
                  sx={{ 
                    color: darkGreenColor, 
                    opacity: 0.5,
                    transition: 'all 0.3s ease',
                    fontSize: 20
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    }

    const completionPercentage = Math.round(environmentBuildData.completionPercentage || 0);
    const completedStatus = Math.round(environmentBuildData.completedStatus || 0);
    const remainingStatus = Math.round(environmentBuildData.remainingStatus || 0);
    const totalStatus = Math.round(environmentBuildData.totalStatus || 0);

    return (
      <Card 
        sx={{ 
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px ${darkGreenColor}40`,
            '& .card-icon': {
              transform: 'scale(1.1)',
            },
            '& .arrow-icon': {
              opacity: 1,
              transform: 'translateX(4px)',
            }
          }
        }}
        elevation={3}
      >
        <CardActionArea 
          onClick={handleEnvironmentBuildClick}
          sx={{ height: '100%', p: 0 }}
        >
          <CardContent sx={{ p: 3, pb: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  className="card-icon"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: `${darkGreenColor}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <BuildIcon sx={{ color: darkGreenColor, fontSize: 32 }} />
                </Box>
                <Typography variant="h5" component="h2" fontWeight={600}>
                  Environment Build
                </Typography>
              </Box>
              <ArrowForwardIcon 
                className="arrow-icon"
                sx={{ 
                  color: darkGreenColor, 
                  opacity: 0.5,
                  transition: 'all 0.3s ease',
                  fontSize: 20
                }} 
              />
            </Box>
            
            <Box mb={2.5}>
              <Typography 
                variant="h2" 
                fontWeight={700}
                sx={{ 
                  color: darkGreenColor,
                  mb: 1,
                  background: `linear-gradient(135deg, ${darkGreenColor} 0%, ${darkGreenColor}dd 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {completionPercentage}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: `${darkGreenColor}20`,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: `linear-gradient(90deg, ${darkGreenColor} 0%, ${darkGreenColor}cc 100%)`
                  }
                }}
              />
            </Box>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Completed
                  </Typography>
                  <Typography variant="h5" fontWeight={600} mt={0.5}>
                    {totalStatus > 0 ? Math.round((completedStatus / totalStatus) * 100) : 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Remaining
                  </Typography>
                  <Typography variant="h5" fontWeight={600} mt={0.5}>
                    {totalStatus > 0 ? Math.round((remainingStatus / totalStatus) * 100) : 0}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gap={0.5}>
        <Typography variant="h4" fontWeight={700}>
          Compliance Progress Overview
        </Typography>
        <IconButton
          size="small"
          color="inherit"
          onClick={(e) => openHelp(e, PROGRESS_HELP)}
          aria-label="How progress is calculated"
          sx={{ color: 'text.secondary' }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} lg={3}>
          {renderControlCard(
            'Privacy Control',
            PrivacyIcon,
            dashboardData?.privacyControl,
            '#1976d2',
            'Privacy Control'
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {renderControlCard(
            'HIPAA Control',
            HipaaIcon,
            dashboardData?.hipaaControl,
            '#d32f2f',
            'HIPAA Control'
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {renderControlCard(
            'Security Control',
            SecurityIcon,
            dashboardData?.securityControl,
            '#ed6c02',
            'Security Control'
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {renderEnvironmentBuildCard()}
        </Grid>
      </Grid>

      {/* Assets Section */}
      <Box mt={4}>
        <Box mb={2} display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h4" fontWeight={700}>
            Assets Overview
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={(e) => openHelp(e, ASSETS_HELP)}
            aria-label="About assets cards"
            sx={{ color: 'text.secondary' }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            {renderAssetsCard(
              'Decisions Progress',
              DecisionsIcon,
              assetsData.decisions,
              '#f57c00',
              'decisions'
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderAssetsCard(
              'Requirement List',
              RequirementListIcon,
              assetsData.requirements,
              '#9c27b0',
              'requirements'
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderAssetsCard(
              'Services',
              ServicesIcon,
              assetsData.services,
              '#0288d1',
              'services'
            )}
          </Grid>
        </Grid>
      </Box>

      <Popover
        open={Boolean(helpAnchor)}
        anchorEl={helpAnchor}
        onClose={closeHelp}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxWidth: 360, p: 2 } }}
      >
        {helpContent}
      </Popover>
    </Box>
  );
};

export default HipaaCuiProgressDashboard;

